/**
 * RoiSettingsCard — "See the Impact" calculator settings inside the New Business builder.
 * Everything here is saved on the proposal as `roi` and read by ProposalRoiCalculator.
 * Master switch shows/hides the whole block; Save money and Make money are switchable on their own.
 */
import { useMemo } from "react";
import { Calculator, PiggyBank, TrendingUp, Ruler, LayoutGrid, FileBadge } from "lucide-react";
import { normaliseRoi, bundleBaseCost, computeRoi, fmtGBP, fmtMoney, snapVisits, roiModes } from "./roiMath";
import RddToggle from "./RddToggle";

const clampInt = (v, lo, hi, fallback) => {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(Math.max(n, lo), hi);
};

function Num({ label, value, onChange, step = 1, min = 0, max, prefix, suffix, testId }) {
  return (
    <label className="rdd-field rdd-roi-field">
      <span className="rdd-field-label">{label}</span>
      <span className="rdd-roi-input">
        {prefix && <span className="rdd-roi-affix">{prefix}</span>}
        <input
          type="number"
          className="rdd-input"
          value={value ?? ""}
          step={step}
          min={min}
          max={max}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          data-testid={testId}
        />
        {suffix && <span className="rdd-roi-affix">{suffix}</span>}
      </span>
    </label>
  );
}

function ModeSelect({ label, value, options, onChange, testId }) {
  return (
    <label className="rdd-field rdd-roi-field">
      <span className="rdd-field-label">{label}</span>
      <select className="rdd-input rdd-roi-select" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

// One of the two calculator views (Save money / Make money) with its own switch
function ModePanel({ icon: Icon, title, hint, on, onToggle, testId, result, children }) {
  return (
    <div className={`rdd-roi-mode ${on ? "is-on" : "is-off"}`} data-testid={testId}>
      <div className="rdd-roi-mode-head">
        <span className="rdd-roi-mode-title"><Icon size={14} /> {title}</span>
        <RddToggle size="sm" checked={on} onChange={onToggle} testId={`${testId}-toggle`} />
      </div>
      <p className="rdd-roi-mode-hint">{hint}</p>
      {on ? (
        <>
          {children}
          <div className="rdd-roi-mode-result" data-testid={`${testId}-result`}>{result}</div>
        </>
      ) : (
        <p className="rdd-roi-mode-off">Hidden from the prospect's page</p>
      )}
    </div>
  );
}

// Additional charge / revenue line with a switch and its own settings
function ExtraRow({ icon: Icon, title, meta, on, onToggle, testId, children }) {
  return (
    <div className={`rdd-roi-extra ${on ? "is-on" : ""}`} data-testid={testId}>
      <div className="rdd-roi-extra-head">
        <RddToggle size="sm" checked={on} onChange={onToggle} testId={`${testId}-enabled`} label={<><Icon size={12} /> {title}</>} hint={meta} />
      </div>
      {on && children}
    </div>
  );
}

export default function RoiSettingsCard({ roi: rawRoi, bundles = [], onChange }) {
  const roi = useMemo(() => normaliseRoi(rawRoi), [rawRoi]);
  const set = (patch) => onChange({ ...roi, ...patch });
  const setSub = (key, patch) => onChange({ ...roi, [key]: { ...roi[key], ...patch } });

  const bundleIdx = Number.isInteger(roi.base_bundle_index) && roi.base_bundle_index < bundles.length ? roi.base_bundle_index : 0;
  const baseCost = bundles.length ? bundleBaseCost(bundles[bundleIdx]) : Number(roi.nichecom_cost_per_visit) || 0;
  const visits = snapVisits(roi.annual_visits_default || 100, roi.visits_max || 10000);
  const preview = computeRoi({ roi, visits, baseCost, currentCost: roi.current_cost_per_visit });
  const masterOn = roi.enabled !== false;
  const modes = roiModes(roi);
  const visitsLabel = visits.toLocaleString("en-GB");

  return (
    <div className={`rdd-card rdd-roi-card ${masterOn ? "" : "is-off"}`} data-testid="rdd-roi-card">
      <div className="rdd-card-head-row">
        <h3 className="rdd-card-title"><Calculator size={15} /> "See the Impact" ROI calculator</h3>
        <RddToggle
          size="sm"
          checked={masterOn}
          onChange={(on) => set({ enabled: on })}
          label={masterOn ? "Shown on the prospect's page" : "Hidden from the prospect's page"}
          hint={masterOn ? (modes.length === 2 ? "Save money + Make money" : modes.length === 1 ? (modes[0] === "save" ? "Save money only" : "Make money only") : "Switch on a view below") : "Package picker + visits slider"}
          testId="rdd-roi-enabled"
        />
      </div>
      <p className="rdd-hint rdd-roi-intro">
        An interactive block on the prospect's page: they drag a visits-per-year slider and see the numbers move.
        Pricing comes from this proposal's packages. Pick which views they get below.
      </p>

      <div className={`rdd-roi-body ${masterOn ? "" : "is-disabled"}`}>
        <div className="rdd-roi-grid rdd-roi-grid-3">
          <Num label="Default annual visits" value={roi.annual_visits_default} min={1} max={10000}
               onChange={(v) => set({ annual_visits_default: v === "" ? "" : clampInt(v, 1, 10000, 100) })} testId="rdd-roi-visits" />
          <Num label="Slider maximum" value={roi.visits_max} min={10} max={10000}
               onChange={(v) => set({ visits_max: v === "" ? "" : clampInt(v, 10, 10000, 10000) })} testId="rdd-roi-visits-max" />
          {bundles.length > 0 ? (
            <ModeSelect
              label="Typical visit = package"
              value={String(bundleIdx)}
              options={bundles.map((b, i) => [String(i), `${b.name || `Package ${i + 1}`} — ${fmtMoney(bundleBaseCost(b))} per visit`])}
              onChange={(v) => set({ base_bundle_index: Number(v) })}
              testId="rdd-roi-bundle"
            />
          ) : (
            <Num label="NicheCom cost per visit (no packages yet)" prefix="£" value={roi.nichecom_cost_per_visit ?? ""}
                 onChange={(v) => set({ nichecom_cost_per_visit: v === "" ? null : v })} testId="rdd-roi-nichecom-cost" />
          )}
        </div>

        <div className="rdd-roi-modes">
          <ModePanel
            icon={PiggyBank}
            title="Save money"
            hint="Compares what they pay per visit today with the NicheCom package."
            on={roi.show_save !== false}
            onToggle={(on) => set({ show_save: on })}
            testId="rdd-roi-mode-save"
            result={<>At {visitsLabel} visits they'd save <strong>{fmtGBP(preview.save.saving)}</strong> a year</>}
          >
            <Num label="Their current cost per visit" prefix="£" value={roi.current_cost_per_visit} step="1"
                 onChange={(v) => set({ current_cost_per_visit: v })} testId="rdd-roi-current-cost" />
          </ModePanel>
          <ModePanel
            icon={TrendingUp}
            title="Make money"
            hint="Shows the margin if they resell each visit to their vendors with an uplift."
            on={roi.show_make !== false}
            onToggle={(on) => set({ show_make: on })}
            testId="rdd-roi-mode-make"
            result={<>At {visitsLabel} visits they'd make <strong>{fmtGBP(preview.make.annualMargin)}</strong> a year</>}
          >
            <Num label="Vendor uplift" suffix="%" value={roi.uplift_pct} step="1" max={500}
                 onChange={(v) => set({ uplift_pct: v })} testId="rdd-roi-uplift" />
          </ModePanel>
        </div>
        {masterOn && modes.length === 0 && (
          <p className="rdd-roi-warn" data-testid="rdd-roi-no-modes">Both views are off, so the calculator won't show. Switch one on or hide the calculator.</p>
        )}

        <h4 className="rdd-roi-subtitle">Additional charges &amp; revenue</h4>

        <ExtraRow
          icon={Ruler}
          title="Square footage charge"
          meta={roi.sqft.enabled ? `Note on the page: £${Number(roi.sqft.rate || 0).toFixed(3)} per sq ft above ${Number(roi.sqft.threshold || 0).toLocaleString("en-GB")} sq ft` : "Off — add a large-property note"}
          on={!!roi.sqft.enabled}
          onToggle={(on) => setSub("sqft", { enabled: on })}
          testId="rdd-roi-sqft"
        >
          <div className="rdd-roi-grid">
            <Num label="Rate per sq ft" prefix="£" value={roi.sqft.rate} step="0.005" onChange={(v) => setSub("sqft", { rate: v })} testId="rdd-roi-sqft-rate" />
            <Num label="Kicks in above (sq ft)" value={roi.sqft.threshold} step="50" onChange={(v) => setSub("sqft", { threshold: v })} testId="rdd-roi-sqft-threshold" />
          </div>
        </ExtraRow>

        <ExtraRow
          icon={LayoutGrid}
          title="Floorplans"
          meta={roi.floorplan.enabled ? `${fmtMoney(roi.floorplan.price)} on ${roi.floorplan.share_pct}% of visits` : "Off"}
          on={!!roi.floorplan.enabled}
          onToggle={(on) => setSub("floorplan", { enabled: on })}
          testId="rdd-roi-floorplan"
        >
          <div className="rdd-roi-grid rdd-roi-grid-3">
            <Num label="Floorplan charge" prefix="£" value={roi.floorplan.price} onChange={(v) => setSub("floorplan", { price: v })} testId="rdd-roi-floorplan-price" />
            <Num label="Visits needing one" suffix="%" value={roi.floorplan.share_pct} max={100} onChange={(v) => setSub("floorplan", { share_pct: v })} testId="rdd-roi-floorplan-share" />
            <ModeSelect label="How it's handled" value={roi.floorplan.mode}
                        options={[["included", "Included in package"], ["agency_pays", "Charged separately (agency pays)"], ["vendor_pays", "Resold to vendor"]]}
                        onChange={(v) => setSub("floorplan", { mode: v })} testId="rdd-roi-floorplan-mode" />
          </div>
        </ExtraRow>

        <ExtraRow
          icon={FileBadge}
          title="EPCs"
          meta={roi.epc.enabled ? `${fmtMoney(roi.epc.price)} on ${roi.epc.share_pct}% of visits` : "Off"}
          on={!!roi.epc.enabled}
          onToggle={(on) => setSub("epc", { enabled: on })}
          testId="rdd-roi-epc"
        >
          <div className="rdd-roi-grid rdd-roi-grid-3">
            <Num label="EPC charge" prefix="£" value={roi.epc.price} onChange={(v) => setSub("epc", { price: v })} testId="rdd-roi-epc-price" />
            <Num label="Visits needing one" suffix="%" value={roi.epc.share_pct} max={100} onChange={(v) => setSub("epc", { share_pct: v })} testId="rdd-roi-epc-share" />
            <ModeSelect label="Who pays" value={roi.epc.mode}
                        options={[["agency_pays", "Agency pays"], ["vendor_pays", "Vendor orders & pays (cost-neutral)"]]}
                        onChange={(v) => setSub("epc", { mode: v })} testId="rdd-roi-epc-mode" />
          </div>
        </ExtraRow>
      </div>
    </div>
  );
}
